import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowsClockwise,
  CheckCircle,
  ForkKnife,
  Info,
  NotePencil,
  Users,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  FOOD_MEAL_TYPES,
  foodMealLabel,
  getMealServiceSnapshot,
  getTeacherFoodDay,
  openMealService,
  recordCheckedInRoster,
  recordMealParticipation,
} from "@/lib/foodProgram";
import { prettyDate, todayStr } from "@/lib/entryTypes";

const inputClass =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-neutral-50";

function statusMeta(status, kind = "participation") {
  if (kind === "service") {
    if (status === "locked") return { label: "Locked", className: "bg-neutral-100 text-neutral-700 border-neutral-300" };
    if (status === "recorded") return { label: "Recorded", className: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    if (status === "in_service") return { label: "In service", className: "bg-primary/10 text-primary-hover border-primary/20" };
    return { label: "Planned", className: "bg-amber-50 text-amber-900 border-amber-200" };
  }
  if (status === "served") return { label: "Served", className: "bg-emerald-50 text-emerald-800 border-emerald-200" };
  if (status === "substituted") return { label: "Substitution served", className: "bg-amber-50 text-amber-900 border-amber-200" };
  if (status === "not_served") return { label: "Not served", className: "bg-neutral-100 text-neutral-700 border-neutral-200" };
  return { label: "Not counted", className: "bg-white text-neutral-600 border-neutral-200" };
}

function isLocked(snapshot) {
  return snapshot?.service?.status === "locked";
}

export default function TeacherFoodProgram() {
  const [serviceDate, setServiceDate] = useState(todayStr());
  const [day, setDay] = useState(null);
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [selectedMealType, setSelectedMealType] = useState("lunch");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [serviceSnapshot, setServiceSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null);
  const [editorNote, setEditorNote] = useState("");
  const editorNoteRef = useRef(null);

  const loadDay = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError("");
    try {
      const data = await getTeacherFoodDay(serviceDate);
      setDay(data);
      setSelectedClassroomId((current) => {
        if (current && data.classrooms?.some((classroom) => classroom.id === current)) return current;
        return data.classrooms?.[0]?.id || "";
      });
    } catch (loadError) {
      setError(loadError.message || "Could not load today’s meal service.");
    } finally {
      if (!background) setLoading(false);
    }
  }, [serviceDate]);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  useEffect(() => {
    if (!editor) return;
    const frame = window.requestAnimationFrame(() => editorNoteRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [editor]);

  const matchingTemplates = useMemo(
    () => (day?.templates || []).filter((template) => template.meal_type === selectedMealType),
    [day?.templates, selectedMealType],
  );

  const existingService = useMemo(
    () =>
      (day?.services || []).find(
        (service) => service.classroom_id === selectedClassroomId && service.meal_type === selectedMealType,
      ) || null,
    [day?.services, selectedClassroomId, selectedMealType],
  );

  useEffect(() => {
    setSelectedTemplateId((current) => {
      if (current && matchingTemplates.some((template) => template.id === current)) return current;
      return existingService?.menu_template_id || matchingTemplates[0]?.id || "";
    });
  }, [existingService?.menu_template_id, matchingTemplates]);

  useEffect(() => {
    let cancelled = false;
    if (!existingService?.id) {
      setServiceSnapshot(null);
      return undefined;
    }
    setServiceLoading(true);
    getMealServiceSnapshot(existingService.id)
      .then((snapshot) => {
        if (!cancelled) setServiceSnapshot(snapshot);
      })
      .catch((snapshotError) => {
        if (!cancelled) setError(snapshotError.message || "Could not load this meal service.");
      })
      .finally(() => {
        if (!cancelled) setServiceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [existingService?.id]);

  const run = async (operation, successMessage) => {
    setSaving(true);
    try {
      const snapshot = await operation();
      if (snapshot?.service) setServiceSnapshot(snapshot);
      await loadDay({ background: true });
      if (successMessage) toast.success(successMessage);
      return snapshot;
    } catch (mutationError) {
      toast.error(mutationError.message || "That change could not be saved.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleOpen = async () => {
    if (!selectedClassroomId) return;
    await run(
      () =>
        openMealService({
          classroomId: selectedClassroomId,
          serviceDate,
          mealType: selectedMealType,
          menuTemplateId: selectedTemplateId || null,
        }),
      `${foodMealLabel(selectedMealType)} is ready to record.`,
    );
  };

  const handleCountCheckedIn = async () => {
    if (!serviceSnapshot?.service?.id) return;
    await run(
      () => recordCheckedInRoster(serviceSnapshot.service.id),
      "Checked-in children were recorded.",
    );
  };

  const openEditor = (child, participationStatus) => {
    setEditor({ child, participationStatus });
    setEditorNote("");
  };

  const handleDirectStatus = async (child, participationStatus) => {
    if (!serviceSnapshot?.service?.id) return;
    if (participationStatus === "substituted" || (!child.attendance_eligible && participationStatus !== "not_served")) {
      openEditor(child, participationStatus);
      return;
    }
    await run(
      () =>
        recordMealParticipation({
          serviceId: serviceSnapshot.service.id,
          childId: child.child_id,
          participationStatus,
        }),
      "Meal record updated.",
    );
  };

  const saveEditor = async () => {
    if (!editor || !serviceSnapshot?.service?.id) return;
    const isException = !editor.child.attendance_eligible && editor.participationStatus !== "not_served";
    const result = await run(
      () =>
        recordMealParticipation({
          serviceId: serviceSnapshot.service.id,
          childId: editor.child.child_id,
          participationStatus: editor.participationStatus,
          substitutionNote: editor.participationStatus === "substituted" ? editorNote : "",
          attendanceExceptionNote: isException ? editorNote : "",
        }),
      "Meal record updated.",
    );
    if (result) {
      setEditor(null);
      setEditorNote("");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24" aria-label="Loading food program"><div className="h-9 w-9 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div>;
  }

  if (error && !day) {
    return (
      <section data-testid="teacher-food-program-error" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex gap-3"><WarningCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-amber-800" /><div><h1 className="font-heading text-lg font-semibold text-amber-950">Food program is unavailable</h1><p className="mt-1 text-sm leading-6 text-amber-900">{error}</p><button type="button" onClick={() => loadDay()} className="mt-3 text-sm font-semibold text-amber-950 underline">Try again</button></div></div>
      </section>
    );
  }

  if (!day?.program) {
    return (
      <section data-testid="teacher-food-program-not-configured" className="rounded-2xl border border-dashed border-neutral-300 bg-white p-7 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary-hover"><ForkKnife size={24} weight="duotone" /></span>
        <h1 className="mt-3 font-heading text-xl font-semibold tracking-tight text-neutral-900">Food program setup is still needed</h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-600">A director can add your center’s food program, menus, and instructions. Until then, continue using your normal daily activity workflow.</p>
      </section>
    );
  }

  if (!selectedClassroomId) {
    return (
      <section data-testid="teacher-food-program-no-classrooms" className="rounded-2xl border border-dashed border-neutral-300 bg-white p-7 text-center">
        <Users size={24} weight="duotone" className="mx-auto text-primary-hover" />
        <h1 className="mt-3 font-heading text-xl font-semibold tracking-tight text-neutral-900">No classroom assignment found</h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-600">Ask your director to check your classroom assignment before recording food service.</p>
      </section>
    );
  }

  const locked = isLocked(serviceSnapshot);
  const roster = serviceSnapshot?.roster || [];
  const summary = serviceSnapshot?.summary;
  const uncheckedEligible = roster.filter((child) => child.attendance_eligible && !child.participation_id).length;

  return (
    <div data-testid="teacher-food-program" className="space-y-5 pb-4">
      <section className="flex items-start justify-between gap-3">
        <div>
          <p className="overline text-primary-hover">{day.program.name}</p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900">Food service</h1>
          <p className="mt-1 text-sm text-neutral-600">Record the classroom meal from today’s attendance—without re-entering the roster.</p>
        </div>
        <button type="button" aria-label="Refresh food service" disabled={saving} onClick={() => loadDay({ background: true })} className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-primary-hover hover:bg-primary/5 disabled:opacity-60"><ArrowsClockwise size={18} weight="bold" className={saving ? "animate-spin" : ""} /></button>
      </section>

      {error ? <section className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{error}</section> : null}

      <section className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-card">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-semibold text-neutral-700">Date<input type="date" value={serviceDate} max={todayStr()} onChange={(event) => setServiceDate(event.target.value)} className={`${inputClass} mt-1`} /></label>
          <label className="text-xs font-semibold text-neutral-700">Classroom<select value={selectedClassroomId} onChange={(event) => setSelectedClassroomId(event.target.value)} className={`${inputClass} mt-1`}>
            {day.classrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroom.name}{classroom.age_group ? ` · ${classroom.age_group}` : ""}</option>)}
          </select></label>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3" role="group" aria-label="Meal type">
          {FOOD_MEAL_TYPES.map((meal) => <button key={meal.key} type="button" onClick={() => setSelectedMealType(meal.key)} className={`rounded-xl border px-2 py-2 text-xs font-semibold transition ${selectedMealType === meal.key ? "border-primary bg-primary text-white" : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"}`}>{meal.label}</button>)}
        </div>
        <label className="mt-4 block text-xs font-semibold text-neutral-700">Planned menu<select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)} disabled={locked} className={`${inputClass} mt-1`}>
          <option value="">No menu selected yet</option>
          {matchingTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
        </select></label>
        {!serviceSnapshot ? (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-primary/5 p-3">
            <p className="text-sm leading-5 text-neutral-700">{prettyDate(serviceDate)} · {foodMealLabel(selectedMealType)}</p>
            <button type="button" onClick={handleOpen} disabled={saving} className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">Start meal</button>
          </div>
        ) : null}
      </section>

      {serviceLoading ? <div className="flex justify-center py-8"><div className="h-7 w-7 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div> : null}

      {serviceSnapshot ? (
        <>
          <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-100 p-4">
              <div>
                <div className="flex flex-wrap items-center gap-2"><h2 className="font-heading text-xl font-semibold tracking-tight text-neutral-900">{foodMealLabel(serviceSnapshot.service.meal_type)}</h2><StatusChip status={serviceSnapshot.service.status} kind="service" /></div>
                <p className="mt-1 text-sm text-neutral-600">{serviceSnapshot.service.menu?.name || "No menu selected"}{serviceSnapshot.service.menu?.description ? ` · ${serviceSnapshot.service.menu.description}` : ""}</p>
              </div>
              {locked ? <span className="rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">Locked after review</span> : null}
            </div>
            {serviceSnapshot.service.menu?.items?.length ? <ul className="divide-y divide-neutral-100 px-4" aria-label="Planned menu items">{serviceSnapshot.service.menu.items.map((item) => <li key={item.id} className="flex items-start justify-between gap-3 py-2.5 text-sm"><span className="font-medium text-neutral-800">{item.item_label}</span><span className="text-right text-xs text-neutral-500">{item.component_label}{item.portion_note ? ` · ${item.portion_note}` : ""}</span></li>)}</ul> : <p className="px-4 py-3 text-sm text-amber-800">No menu is attached. A director can review this service before export.</p>}
            <div className="grid grid-cols-3 gap-2 border-t border-neutral-100 p-4"><Metric label="Counted" value={summary?.counted_total || 0} /><Metric label="Substitutions" value={summary?.substituted_count || 0} /><Metric label="Exceptions" value={summary?.attendance_exception_count || 0} warning={Boolean(summary?.attendance_exception_count)} /></div>
          </section>

          {!locked ? <button type="button" data-testid="teacher-food-count-checked-in" onClick={handleCountCheckedIn} disabled={saving || uncheckedEligible === 0} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-60"><CheckCircle size={18} weight="bold" />{uncheckedEligible ? `Record checked-in roster (${uncheckedEligible} remaining)` : "Checked-in roster recorded"}</button> : null}

          <section className="rounded-2xl border border-neutral-200 bg-white shadow-card">
            <div className="border-b border-neutral-100 p-4"><h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">Classroom roster</h2><p className="mt-1 text-sm text-neutral-600">Attendance supplies the normal count. Add an exception only when the recorded attendance does not reflect service.</p></div>
            {roster.length === 0 ? <p className="p-5 text-sm text-neutral-600">No enrolled children were found for this classroom and date.</p> : <ul className="divide-y divide-neutral-100">{roster.map((child) => <RosterRow key={child.child_id} child={child} disabled={saving || locked} onStatus={(status) => handleDirectStatus(child, status)} />)}</ul>}
          </section>

          {editor ? <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="teacher-food-exception-editor"><div className="flex items-start gap-3"><NotePencil size={20} weight="duotone" className="mt-0.5 text-amber-800" /><div className="min-w-0 flex-1"><h2 className="font-heading text-base font-semibold text-amber-950">{editor.participationStatus === "substituted" ? "Describe the substitution" : "Record an attendance exception"}</h2><p className="mt-1 text-sm leading-5 text-amber-900">{editor.participationStatus === "substituted" ? `What was served for ${editor.child.first_name}?` : `${editor.child.first_name} is not marked present or late. Explain why this meal should still be recorded.`}</p><label className="mt-3 block text-xs font-semibold text-amber-950"><span className="sr-only">Required note</span><textarea ref={editorNoteRef} value={editorNote} onChange={(event) => setEditorNote(event.target.value)} rows={3} aria-required="true" placeholder={editor.participationStatus === "substituted" ? "Example: sunflower-seed butter sandwich" : "Example: attendance check-in was corrected with the director"} className={`${inputClass} mt-1`} /></label><div className="mt-3 flex gap-2"><button type="button" onClick={saveEditor} disabled={saving || !editorNote.trim()} className="rounded-full bg-amber-900 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-950 disabled:opacity-60">Save record</button><button type="button" onClick={() => { setEditor(null); setEditorNote(""); }} disabled={saving} className="rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100">Cancel</button></div></div></div></section> : null}
        </>
      ) : null}

      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4"><div className="flex gap-3"><Info size={19} weight="duotone" className="mt-0.5 shrink-0 text-primary-hover" /><p className="text-sm leading-6 text-neutral-700">HoneyAcorn records the meal service and the attendance context it used. Review local food-program requirements with your center before filing any reimbursement claim.</p></div></section>
    </div>
  );
}

function StatusChip({ status, kind }) {
  const meta = statusMeta(status, kind);
  return <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${meta.className}`}>{meta.label}</span>;
}

function Metric({ label, value, warning = false }) {
  return <div><p className="text-[10px] font-bold uppercase tracking-[0.11em] text-neutral-500">{label}</p><p className={`mt-1 font-heading text-xl font-semibold ${warning ? "text-amber-800" : "text-neutral-900"}`}>{value}</p></div>;
}

function RosterRow({ child, disabled, onStatus }) {
  const status = statusMeta(child.participation_status);
  return (
    <li className="p-4">
      <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="font-semibold text-neutral-900">{child.first_name} {child.last_name}</p><div className="mt-1 flex flex-wrap gap-1.5">{child.allergies ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-800">Allergy alert</span> : null}{(child.accommodations || []).map((accommodation) => <span key={accommodation.id} title={accommodation.teacher_instruction} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900">{accommodation.label}</span>)}{!child.attendance_eligible ? <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-600">Not checked in</span> : null}</div></div><span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ${status.className}`}>{status.label}</span></div>
      {(child.accommodations || []).map((accommodation) => <p key={`${accommodation.id}-instruction`} className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs leading-5 text-amber-950">{accommodation.teacher_instruction}</p>)}
      {!disabled ? <div className="mt-3 flex flex-wrap gap-2"><button type="button" onClick={() => onStatus("served")} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-100">Served</button><button type="button" onClick={() => onStatus("substituted")} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100">Substitution</button><button type="button" onClick={() => onStatus("not_served")} className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">Not served</button></div> : null}
    </li>
  );
}
